import { useCallback, useEffect, useState } from 'react'
import { Languages } from 'lucide-react'
import {
  Bidi, Button, Card, CardHeader, ErrorState, FieldInput, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  fetchTranslations, formatCount, may, saveTranslation, translateError,
  type TranslatedKind, type TranslationRow, type TranslationView,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-128 · EVERY WORD THIS PRODUCT INVENTED.  Sprint 59.

   ── WHY THIS SCREEN EXISTS ───────────────────────────────────────────────────────────
   Every Arabic name in this build is OURS. A developer wrote "غرفة لأربعة" for a room type,
   "منشفة حمام" for a towel, "دورة مياه" for an area, and each one was a guess at what this
   hotel calls the thing. CQ-130 settled what happens about that on 18 September: **the
   invented words stay, and the hotel corrects them during go-live.**

   Sprints 55 and 56 made that true for four kinds of word. **Nine more had an Arabic column,
   an invented value in it, and no screen anywhere that could write it** — which is why
   `admin.translations` read "nothing yet — W-128 is not built" in the permission catalogue
   from the day the catalogue was written until this sprint.

   ── ONE EDITABLE COLUMN, AND THE ENGLISH BESIDE IT ───────────────────────────────────
   The English is SHOWN and not offered. It is what makes the Arabic box answerable by
   somebody who does not know what a row is for — "Bath towel" tells you what `TOWEL_BATH`
   is — and changing it would move every report's grouping label, which is a different act.

   ── THE COUNT IS WHY ANYBODY OPENS IT ────────────────────────────────────────────────
   A list of ninety words is something to scroll. *"Fourteen still to do"* is a job somebody
   can finish in a morning, which is what CQ-130's answer actually asks of the hotel.
   ========================================================================= */

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole)
}

function pluralOf(forms: Record<string, string>, count: number, locale: 'en' | 'ar'): string {
  return forms[new Intl.PluralRules(locale).select(count)] ?? forms.other ?? ''
}

/**
 * **WHAT THE COUNT ON A ROW IS A COUNT OF.** Only three kinds have anything to count: a
 * building has floors, a zone has areas, a room type has rooms. The other six are one thing
 * each, which is why they send null and get no line — see the note on the table.
 */
const INUSE_NOUN: Partial<Record<TranslatedKind, (t: typeof STRINGS.en) => Record<string, string>>> = {
  BUILDING: (t) => t.trInUseFloors,
  ZONE: (t) => t.trInUseAreas,
  ROOM_TYPE: (t) => t.trInUseRooms,
}

export function Translations({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [view, setView] = useState<TranslationView | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)

  const allowed = may('admin.translations')

  const take = useCallback((next: TranslationView) => {
    setView(next)
    setDrafts(Object.fromEntries(
      next.groups.flatMap((g) => g.rows.map((r) => [r.id, r.nameAr ?? '']))))
  }, [])

  const load = useCallback(async () => {
    if (!allowed) return
    setError(null)
    try {
      take(await fetchTranslations(propertyId))
    } catch (caught: unknown) {
      setError(caught)
    }
  }, [propertyId, allowed, take])

  useEffect(() => { void load() }, [load])

  if (!allowed) return <NoPermissionState />
  if (error !== null && view === null) {
    return <ErrorState body={translateError(error, locale).message} onRetry={() => void load()} />
  }
  if (view === null) return <LoadingState rows={6} />

  async function commit(row: TranslationRow) {
    setSaving(row.id); setError(null)
    try {
      take(await saveTranslation(
        propertyId, row.kind, row.id, drafts[row.id] ?? ''))
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setSaving(null)
    }
  }

  const changed = (row: TranslationRow) =>
    (drafts[row.id] ?? '') !== (row.nameAr ?? '')

  const remaining = view.total - view.translated

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader title={t.trTitle} icon={<Languages className="size-4" />} />
        <div className="flex flex-col gap-2 px-3.5 pb-3.5">
          <p className="max-w-[70ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.trIntro}
          </p>

          {/* **THE FIGURE, AND THE THIRD CASE.** Complete, some remaining, and NOTHING
              LOADED — which is neither. A hotel part-way through go-live with no stock
              yet has translated nothing and is missing nothing, and a bar reading 0%
              would send somebody looking for work that does not exist. */}
          <div
            role="status"
            className={`rounded-hk border px-3 py-2 text-[12.5px] ${
              view.total === 0
                ? 'border-border bg-muted/40 text-muted-foreground'
                : remaining > 0
                  ? 'border-[#fcd34d] bg-[#fffbeb] text-[#b45309] dark:border-[#b45309] dark:bg-[#2a1e08] dark:text-[#fbbf24]'
                  : 'border-[#86efac] bg-[#f0fdf4] text-[#166534] dark:border-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]'}`}
          >
            <Bidi>
              {view.total === 0
                ? t.trNothingToDo
                : remaining > 0
                  ? fill(pluralOf(t.trRemaining, remaining, locale),
                      { count: formatCount(remaining, locale) })
                  : t.trAllDone}
            </Bidi>
          </div>
        </div>
      </Card>

      {error !== null ? (
        <div
          role="alert"
          className="rounded-hk border border-[#fca5a5] bg-[#fef2f2] px-3 py-2 text-[12px]
                     text-[#b91c1c] dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]"
        >
          {String(translateError(error, locale).message)}
        </div>
      ) : null}

      {view.groups.map((group) => (
        <Card key={group.kind}>
          <CardHeader
            title={t.trKind[group.kind] ?? group.kind}
            /* Its own count, because a person works down this screen one group at a time
               and the figure at the top cannot say which group is the unfinished one. */
            subtitle={group.total === 0
              ? undefined
              : `${formatCount(group.translated, locale)} / ${formatCount(group.total, locale)}`}
          />

          {group.rows.length === 0 ? (
            <p className="px-3.5 pb-3.5 text-[12.5px] text-muted-foreground">
              {t.trGroupEmpty}
            </p>
          ) : (
            <div className="overflow-x-auto p-3.5">
              {/* **NO "USED BY" COLUMN**, and the first version of this screen had one.

                  It was right for three kinds — forty rooms of a type, nine areas in a zone
                  — and EMPTY FOR THE OTHER SIX, because an area is one area and a shift is
                  one shift: there is nothing to count. Rendered, that was a column of
                  dashes running the length of the page, which is worse than absent: a
                  reader has to work out that the dash means "not applicable" rather than
                  "none", and does that once per row.

                  The number now sits under the English name on the rows that have one.
                  Found by looking at the rendered screen, which is the only way this kind
                  of thing is ever found. */}
              <table className="w-full min-w-[40rem] table-fixed text-[12.5px]">
                <colgroup>
                  <col className="w-[10rem]" />
                  <col className="w-[16rem]" />
                  <col className="w-[16rem]" />
                  <col className="w-[5.5rem]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="p-2 text-start font-medium">{t.trColCode}</th>
                    <th className="p-2 text-start font-medium">{t.trColName}</th>
                    <th className="p-2 text-start font-medium">{t.trColNameAr}</th>
                    <th className="p-2 text-start font-medium sr-only">{t.trSave}</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row) => (
                    <tr key={row.id} className="border-b border-border/60 last:border-0">
                      <td className="p-2 font-mono text-[11.5px] text-muted-foreground">
                        {row.code}
                      </td>

                      {/* **TEXT, NOT A BOX.** There is no control here to disable, because
                          a disabled control is a question somebody has to ask. */}
                      <td className="p-2">
                        <bdi>{row.name}</bdi>
                        {/* **HOW MANY OF THE THING THERE ARE**, where there is anything to
                            count. Correcting the word on forty rooms and correcting one on
                            a row nobody uses are different acts, and nothing else on the
                            row would say which this is. */}
                        {row.inUse === null ? null : (
                          <div className="mt-0.5 text-[11px] text-muted-foreground">
                            <Bidi>
                              {fill(pluralOf(INUSE_NOUN[row.kind]!(t), row.inUse, locale),
                                { count: formatCount(row.inUse, locale) })}
                            </Bidi>
                          </div>
                        )}
                      </td>

                      <td className="p-2">
                        {/* Each box asks its own row's question — #152. A column of boxes
                            all announcing "What you call it" is a column nobody using a
                            screen reader can tell apart. */}
                        <FieldInput
                          aria-label={`${t.trColNameAr} — ${row.code}`}
                          dir="rtl"
                          lang="ar"
                          maxLength={200}
                          value={drafts[row.id] ?? ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [row.id]: e.target.value }))}
                        />
                      </td>

                      <td className="p-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={!changed(row) || saving === row.id}
                          aria-busy={saving === row.id}
                          aria-label={`${t.trSave} — ${row.code}`}
                          onClick={() => void commit(row)}
                        >
                          {saving === row.id ? t.trSaving : t.trSave}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ))}

      {/* **WHERE THE REST OF THE WORDS ARE.** Said once, at the bottom: somebody who cannot
          find "Room attendant" on this screen should be told where it is rather than left
          hunting, or concluding the screen is incomplete. */}
      <p className="max-w-[70ch] px-1 text-[11.5px] leading-relaxed text-muted-foreground">
        {t.trElsewhere}
      </p>
    </div>
  )
}

/** Exported for the screen contract test — see `headerContract.test.ts`. */
export type { TranslatedKind }
